import { useProctoredAttempt } from '../../components/focus-guard/FocusGuardContext';
/**
 * Inside Sales Training - Session Page (Redesigned)
 * Voice-only interactive training with visible content while AI speaks
 * Proper audio state management to prevent overlapping
 * Real-time speech coaching for instant feedback
 * Gamification with live scores and achievements
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import ConstructiveFeedback, { QuickFeedback } from './ConstructiveFeedback';
import RetryPanel from './components/RetryPanel';
import SpeechCoaching from '../../components/ai/SpeechCoaching';
import useSpeechAnalytics from '../../hooks/useSpeechAnalytics';
import { 
    AnimatedScore, 
    LiveScoreCard, 
    SessionProgressTracker,
    VoiceFeedbackPulse,
    AchievementPopup 
} from '../../components/gamification';
import { 
    Play, Pause, Mic, MicOff, ChevronRight, ChevronLeft,
    BookOpen, HelpCircle, Users, CheckCircle2, XCircle,
    Volume2, VolumeX, RotateCcw, Loader2, Award, Square,
    StopCircle, Clock, Target, Lightbulb, MessageSquare, Trophy,
    Star, MessageCircle
} from 'lucide-react';

// Audio States - Only ONE can be active at a time
const AUDIO_STATE = {
    IDLE: 'idle',
    AI_SPEAKING: 'ai_speaking',
    USER_RECORDING: 'user_recording',
    PROCESSING: 'processing'
};

// Session Stages
const STAGES = {
    INTRO: 'intro',
    TEACHING: 'teaching',
    QUIZ: 'quiz',
    SIMULATION: 'simulation',
    COMPLETE: 'complete'
};

const InsideSalesSession = () => {
    const { sessionId } = useParams();
    const navigate = useNavigate();
    const { api } = useAuth();
    
    // Session state
    const [loading, setLoading] = useState(true);
    const [sessionData, setSessionData] = useState(null);
    const [currentStage, setCurrentStage] = useState(STAGES.INTRO);
    useProctoredAttempt({ active: currentStage === STAGES.SIMULATION || currentStage === STAGES.QUIZ, label: 'Inside Sales session' });
    const [stageLoading, setStageLoading] = useState(false);
    
    // Audio state management - CRITICAL for preventing overlap
    const [audioState, setAudioState] = useState(AUDIO_STATE.IDLE);
    
    // Content display state (what user sees while AI speaks)
    const [displayContent, setDisplayContent] = useState({
        title: '',
        text: '',
        keyPoints: [],
        tips: []
    });
    
    // Teaching state
    const [teachingSections, setTeachingSections] = useState([]);
    const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
    
    // Quiz state
    const [quizQuestions, setQuizQuestions] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [quizResponses, setQuizResponses] = useState([]);
    const [answerFeedback, setAnswerFeedback] = useState(null);
    const [quizResult, setQuizResult] = useState(null);
    const [canFinishSession, setCanFinishSession] = useState(false);
    const [completionError, setCompletionError] = useState(null);
    
    // Simulation state
    const [simulations, setSimulations] = useState([]);
    const [currentSimIndex, setCurrentSimIndex] = useState(0);
    const [simConversation, setSimConversation] = useState([]);
    const [simStarted, setSimStarted] = useState(false);
    const [simComplete, setSimComplete] = useState(false);
    const [simEvaluation, setSimEvaluation] = useState(null);
    
    // Recording state
    const [recordingTime, setRecordingTime] = useState(0);
    const [currentTranscript, setCurrentTranscript] = useState('');
    const [audioError, setAudioError] = useState(null);
    
    // Speech Analytics
    const { 
        analytics: speechAnalytics, 
        isAnalyzing: isSpeechAnalyzing, 
        analyzeTranscript: analyzeSpeech,
        saveAnalyticsToHistory,
        clearAnalytics: clearSpeechAnalytics
    } = useSpeechAnalytics();
    
    // Live Score State - for gamification
    const [liveScores, setLiveScores] = useState({
        voiceScore: 0,
        communicationScore: 0,
        overallScore: 0,
        quizScore: 0,
        simulationScore: 0
    });
    
    // Achievement state
    const [earnedAchievement, setEarnedAchievement] = useState(null);
    
    // Refs
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const timerRef = useRef(null);
    const audioRef = useRef(null);
    const streamRef = useRef(null);

    const cleanup = useCallback(() => {
        stopAudio();
        stopRecording(false);
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
        }
        if (timerRef.current) {
            clearInterval(timerRef.current);
        }
    }, []);

    const fetchSessionData = useCallback(async () => {
        try {
            const [sessionRes, teachingRes, quizRes, simRes] = await Promise.all([
                api.get(`/inside-sales/session/${sessionId}`),
                api.get(`/inside-sales/session/${sessionId}/teaching`),
                api.get(`/inside-sales/session/${sessionId}/quiz`),
                api.get(`/inside-sales/session/${sessionId}/simulations`)
            ]);
            
            setSessionData(sessionRes.data);
            setTeachingSections(teachingRes.data.sections || []);
            setQuizQuestions(quizRes.data.questions || []);
            const sims = simRes.data.simulations || [];
            setSimulations(sims);
            
            // Resume at practice stage if the quiz was already passed
            const progress = sessionRes.data.progress;
            if (progress?.status !== 'completed' && progress?.components?.quiz?.status === 'completed') {
                const firstIncomplete = sims.findIndex(s => !s.completed);
                setCurrentSimIndex(firstIncomplete === -1 ? 0 : firstIncomplete);
                setCanFinishSession(sims.length > 0 && firstIncomplete === -1);
                setCurrentStage(STAGES.SIMULATION);
            }
            
            // Start the session
            await api.post(`/inside-sales/session/${sessionId}/start`).catch(() => null);
        } catch (error) {
            console.error('Failed to load session:', error);
        } finally {
            setLoading(false);
        }
    // api is re-created on every auth render; depending on it would refetch and reset the stage mid-session
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId]);

    useEffect(() => {
        fetchSessionData();
        return () => {
            cleanup();
        };
    }, [fetchSessionData, cleanup]);

    // ==================== AUDIO MANAGEMENT ====================
    // CRITICAL: Only one audio source can be active at a time
    
    const stopAudio = useCallback(() => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            audioRef.current = null;
        }
        if (audioState === AUDIO_STATE.AI_SPEAKING) {
            setAudioState(AUDIO_STATE.IDLE);
        }
    }, [audioState]);

    const playAudio = useCallback(async (audioUrl, onComplete) => {
        // Stop any existing audio first
        stopAudio();
        
        if (!audioUrl) {
            onComplete?.();
            return;
        }
        
        setAudioState(AUDIO_STATE.AI_SPEAKING);
        
        const audio = new Audio(audioUrl);
        audioRef.current = audio;
        
        audio.onended = () => {
            setAudioState(AUDIO_STATE.IDLE);
            onComplete?.();
        };
        
        audio.onerror = (e) => {
            console.error('Audio playback error:', e);
            setAudioState(AUDIO_STATE.IDLE);
            setAudioError('Failed to play audio');
            onComplete?.();
        };
        
        try {
            await audio.play();
        } catch (err) {
            console.error('Audio play failed:', err);
            setAudioState(AUDIO_STATE.IDLE);
            setAudioError('Failed to play audio. Please try again.');
            onComplete?.();
        }
    }, [stopAudio]);

    // ==================== VOICE RECORDING ====================
    
    const startRecording = async () => {
        // Cannot record while AI is speaking
        if (audioState === AUDIO_STATE.AI_SPEAKING) {
            setAudioError('Please wait for the AI to finish speaking');
            return;
        }
        
        setAudioError(null);
        
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ 
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    sampleRate: 44100
                }
            });
            
            streamRef.current = stream;
            mediaRecorderRef.current = new MediaRecorder(stream, {
                mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
            });
            audioChunksRef.current = [];
            
            mediaRecorderRef.current.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };
            
            mediaRecorderRef.current.start(1000);
            setAudioState(AUDIO_STATE.USER_RECORDING);
            setRecordingTime(0);
            setCurrentTranscript('');
            
            timerRef.current = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);
        } catch (error) {
            console.error('Failed to start recording:', error);
            setAudioError('Please allow microphone access to use voice features.');
        }
    };

    const stopRecording = useCallback(async (shouldTranscribe = true) => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        
        if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') {
            setAudioState(AUDIO_STATE.IDLE);
            return null;
        }
        
        return new Promise((resolve) => {
            mediaRecorderRef.current.onstop = async () => {
                if (streamRef.current) {
                    streamRef.current.getTracks().forEach(track => track.stop());
                    streamRef.current = null;
                }
                
                if (audioChunksRef.current.length === 0 || !shouldTranscribe) {
                    setAudioState(AUDIO_STATE.IDLE);
                    resolve(null);
                    return;
                }
                
                const audioBlob = new Blob(audioChunksRef.current, { 
                    type: mediaRecorderRef.current?.mimeType || 'audio/webm' 
                });
                
                setAudioState(AUDIO_STATE.PROCESSING);
                resolve(audioBlob);
            };
            
            mediaRecorderRef.current.stop();
        });
    }, []);

    const transcribeAudio = async (audioBlob) => {
        if (!audioBlob) return '';
        
        try {
            const formData = new FormData();
            formData.append('audio', audioBlob, 'recording.webm');
            
            const response = await api.post('/inside-sales/transcribe', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 60000
            });
            
            setAudioState(AUDIO_STATE.IDLE);
            
            if (response.data.success && response.data.transcript) {
                setCurrentTranscript(response.data.transcript);
                return response.data.transcript;
            } else {
                setAudioError(response.data.error || 'Failed to transcribe audio');
                return '';
            }
        } catch (error) {
            console.error('Transcription error:', error);
            setAudioState(AUDIO_STATE.IDLE);
            setAudioError('Failed to transcribe your response. Please try again.');
            return '';
        }
    };

    // ==================== INTRO STAGE ====================
    
    const playIntro = async () => {
        setStageLoading(true);
        setAudioError(null);
        
        try {
            const response = await api.post(`/inside-sales/session/${sessionId}/tts-intro`);
            
            // Update display content so user can see/read along
            setDisplayContent({
                title: response.data.session_title || `Session ${sessionId}`,
                text: response.data.content || '',
                keyPoints: response.data.learning_objectives || [],
                tips: []
            });
            
            if (response.data.audio_url) {
                playAudio(response.data.audio_url);
            }
        } catch (error) {
            console.error('Failed to load intro:', error);
            setAudioError('Failed to load introduction');
        } finally {
            setStageLoading(false);
        }
    };

    const completeIntro = async () => {
        stopAudio();
        try {
            await api.post(`/inside-sales/session/${sessionId}/intro/complete`);
        } catch (e) {
            console.error('Failed to mark intro complete:', e);
        }
        setCurrentStage(STAGES.TEACHING);
        setDisplayContent({ title: '', text: '', keyPoints: [], tips: [] });
        loadTeachingSection(0);
    };

    // ==================== TEACHING STAGE ====================
    
    const loadTeachingSection = async (index) => {
        if (!teachingSections[index]) return;
        
        setStageLoading(true);
        setAudioError(null);
        stopAudio();
        setCurrentSectionIndex(index);
        
        try {
            const section = teachingSections[index];
            const response = await api.post(
                `/inside-sales/session/${sessionId}/teaching/${section.id}/tts`
            );
            
            // Update display content - THIS IS KEY for user to see content
            setDisplayContent({
                title: response.data.section_title || section.title,
                text: response.data.content || '',
                keyPoints: response.data.key_points || section.key_points || [],
                tips: []
            });
            
            if (response.data.audio_url) {
                playAudio(response.data.audio_url);
            }
        } catch (error) {
            console.error('Failed to load teaching section:', error);
            setAudioError('Failed to load section');
        } finally {
            setStageLoading(false);
        }
    };

    const nextTeachingSection = () => {
        stopAudio();
        if (currentSectionIndex < teachingSections.length - 1) {
            loadTeachingSection(currentSectionIndex + 1);
        } else {
            completeTeaching();
        }
    };

    const completeTeaching = async () => {
        try {
            await api.post(`/inside-sales/session/${sessionId}/teaching/complete`);
        } catch (e) {
            console.error('Failed to mark teaching complete:', e);
        }
        setDisplayContent({ title: '', text: '', keyPoints: [], tips: [] });
        setCurrentStage(STAGES.QUIZ);
        if (quizQuestions[0]) {
            loadQuizQuestion(0);
        }
    };

    // ==================== QUIZ STAGE ====================
    
    const loadQuizQuestion = async (index) => {
        if (!quizQuestions[index]) return;
        
        // IMPORTANT: Stop any currently playing audio first to prevent overlap
        stopAudio();
        
        setStageLoading(true);
        setAnswerFeedback(null);
        setCurrentTranscript('');
        setCurrentQuestionIndex(index);
        
        // Small delay to ensure audio has fully stopped
        await new Promise(resolve => setTimeout(resolve, 100));
        
        try {
            const question = quizQuestions[index];
            
            // Set display content for the question
            setDisplayContent({
                title: `Question ${index + 1} of ${quizQuestions.length}`,
                text: question.question,
                keyPoints: [],
                tips: question.context ? [`Context: ${question.context}`] : []
            });
            
            // Get TTS for the question
            const response = await api.post(
                `/inside-sales/session/${sessionId}/quiz/${question.id}/tts-question`
            );
            
            if (response.data.audio_url) {
                playAudio(response.data.audio_url);
            }
        } catch (error) {
            console.error('Failed to load question:', error);
        } finally {
            setStageLoading(false);
        }
    };

    const handleQuizRecordingStop = async () => {
        const audioBlob = await stopRecording();
        if (!audioBlob) {
            setAudioError('No audio recorded. Please try again.');
            return;
        }
        
        const transcript = await transcribeAudio(audioBlob);
        if (!transcript) return;
        
        // Analyze speech for coaching feedback AND save to history
        analyzeSpeech(transcript, recordingTime, true, 'inside_sales', sessionId);
        
        await submitQuizAnswer(transcript);
    };

    const submitQuizAnswer = async (transcript) => {
        if (!transcript.trim()) return;
        
        setStageLoading(true);
        try {
            const question = quizQuestions[currentQuestionIndex];
            const response = await api.post(`/inside-sales/session/${sessionId}/quiz/answer`, {
                question_id: question.id,
                audio_transcript: transcript
            });
            
            const evaluation = response.data.evaluation;
            setAnswerFeedback(evaluation);
            setQuizResponses(prev => [...prev, {
                question_id: question.id,
                score: evaluation.score
            }]);
            
            // Update live scores with quiz progress
            const newQuizScore = [...quizResponses, { score: evaluation.score }]
                .reduce((sum, r) => sum + (r.score || 0), 0) / (quizResponses.length + 1) * 100;
            
            setLiveScores(prev => ({
                ...prev,
                quizScore: Math.round(newQuizScore),
                overallScore: Math.round((newQuizScore + prev.simulationScore) / 2)
            }));
            
            // Generate feedback text and play TTS
            const feedbackText = `${evaluation.feedback || ''} ${evaluation.encouragement || ''}`;
            
            // Update display with feedback
            setDisplayContent({
                title: evaluation.is_correct ? 'Great Answer!' : 'Keep Learning',
                text: feedbackText,
                keyPoints: evaluation.correct_answer_hint ? [`Hint: ${evaluation.correct_answer_hint}`] : [],
                tips: []
            });
            
            const ttsResponse = await api.post(`/inside-sales/session/${sessionId}/tts-feedback`, null, {
                params: { text: feedbackText }
            });
            
            if (ttsResponse.data.audio_url) {
                playAudio(ttsResponse.data.audio_url);
            }
        } catch (error) {
            console.error('Failed to submit answer:', error);
            setAudioError('Failed to evaluate your answer. Please try again.');
        } finally {
            setStageLoading(false);
        }
    };

    const nextQuestion = () => {
        stopAudio();
        setCurrentTranscript('');
        
        if (currentQuestionIndex < quizQuestions.length - 1) {
            setAnswerFeedback(null);
            loadQuizQuestion(currentQuestionIndex + 1);
        } else {
            completeQuiz();
        }
    };

    const completeQuiz = async () => {
        setStageLoading(true);
        setAudioError(null);
        try {
            const response = await api.post(`/inside-sales/session/${sessionId}/quiz/complete`);
            setAnswerFeedback(null);
            setQuizResult(response.data);
            if (response.data.passed) {
                setDisplayContent({
                    title: 'Quiz Passed!',
                    text: `You scored ${Math.round((response.data.quiz_score || 0) * 100)}% on the quiz. Now let's put your knowledge into practice with a live simulation.`,
                    keyPoints: [],
                    tips: []
                });
                setCurrentStage(STAGES.SIMULATION);
            } else {
                setDisplayContent({ title: '', text: '', keyPoints: [], tips: [] });
            }
        } catch (error) {
            console.error('Failed to complete quiz:', error);
            setAudioError(error.response?.data?.detail || 'Failed to submit the quiz. Please try again.');
        } finally {
            setStageLoading(false);
        }
    };

    const retryQuiz = async () => {
        stopAudio();
        try {
            await api.post(`/inside-sales/session/${sessionId}/retry/quiz`);
        } catch (error) {
            console.error('Failed to reset quiz:', error);
        }
        setQuizResult(null);
        setQuizResponses([]);
        setAnswerFeedback(null);
        setCompletionError(null);
        setSessionData(prev => ({ ...prev, completion: null }));
        setLiveScores(prev => ({ ...prev, quizScore: 0, overallScore: Math.round(prev.simulationScore / 2) }));
        setCurrentStage(STAGES.QUIZ);
        loadQuizQuestion(0);
    };

    // ==================== SIMULATION STAGE ====================
    
    const startSimulation = async () => {
        const simulation = simulations[currentSimIndex];
        if (!simulation) {
            completeSession();
            return;
        }
        
        // Stop any currently playing audio
        stopAudio();
        setStageLoading(true);
        
        try {
            const response = await api.post(
                `/inside-sales/session/${sessionId}/simulation/${simulation.id}/start`
            );
            
            const aiMessage = response.data.ai_message;
            setSimConversation([{ speaker: 'ai', content: aiMessage }]);
            setSimStarted(true);
            
            // Display the AI's opening message
            setDisplayContent({
                title: simulation.title,
                text: aiMessage,
                keyPoints: [],
                tips: [`Your role: ${simulation.user_role}`, `AI plays: ${simulation.ai_role}`]
            });
            
            // Play TTS
            const ttsResponse = await api.post(`/inside-sales/session/${sessionId}/tts-feedback`, null, {
                params: { text: aiMessage }
            });
            if (ttsResponse.data.audio_url) {
                playAudio(ttsResponse.data.audio_url);
            }
        } catch (error) {
            console.error('Failed to start simulation:', error);
            setAudioError('Failed to start simulation. Please try again.');
        } finally {
            setStageLoading(false);
        }
    };

    const handleSimulationRecordingStop = async () => {
        const audioBlob = await stopRecording();
        if (!audioBlob) {
            setAudioError('No audio recorded. Please try again.');
            return;
        }
        
        const transcript = await transcribeAudio(audioBlob);
        if (!transcript) return;
        
        // Analyze speech for coaching feedback AND save to history
        analyzeSpeech(transcript, recordingTime, true, 'inside_sales', sessionId);
        
        await sendSimulationMessage(transcript);
    };

    const sendSimulationMessage = async (transcript) => {
        if (!transcript.trim()) return;
        
        const simulation = simulations[currentSimIndex];
        setStageLoading(true);
        
        // Add user message to conversation
        setSimConversation(prev => [...prev, { speaker: 'user', content: transcript }]);
        
        try {
            const response = await api.post(
                `/inside-sales/session/${sessionId}/simulation/${simulation.id}/message`,
                {
                    scenario_id: simulation.id,
                    audio_transcript: transcript
                }
            );
            
            const aiMessage = response.data.ai_message;
            
            // Add AI response
            setSimConversation(prev => [...prev, { speaker: 'ai', content: aiMessage }]);
            
            // Update display
            setDisplayContent(prev => ({
                ...prev,
                text: aiMessage
            }));
            
            // Play TTS
            const ttsResponse = await api.post(`/inside-sales/session/${sessionId}/tts-feedback`, null, {
                params: { text: aiMessage }
            });
            if (ttsResponse.data.audio_url) {
                playAudio(ttsResponse.data.audio_url);
            }
            
            if (response.data.is_complete) {
                setSimComplete(true);
                setSimEvaluation(response.data.evaluation);
            }
            
            setCurrentTranscript('');
        } catch (error) {
            console.error('Failed to send message:', error);
            setAudioError('Failed to process your response. Please try again.');
        } finally {
            setStageLoading(false);
        }
    };

    const nextSimulation = () => {
        stopAudio();
        setSimStarted(false);
        setSimComplete(false);
        setSimConversation([]);
        setSimEvaluation(null);
        setCurrentTranscript('');
        setDisplayContent({ title: '', text: '', keyPoints: [], tips: [] });
        
        if (currentSimIndex < simulations.length - 1) {
            setCurrentSimIndex(prev => prev + 1);
        } else {
            completeSession();
        }
    };

    // ==================== SESSION COMPLETION ====================
    
    const completeSession = async () => {
        setCompletionError(null);
        try {
            const response = await api.post(`/inside-sales/session/${sessionId}/complete`);
            setSessionData(prev => ({ ...prev, completion: response.data }));
        } catch (error) {
            console.error('Failed to complete session:', error);
            setCompletionError(error.response?.data?.detail || 'Failed to save your session results.');
        }
        setCanFinishSession(false);
        setCurrentStage(STAGES.COMPLETE);
    };

    const retrySimulations = async () => {
        stopAudio();
        try {
            await api.post(`/inside-sales/session/${sessionId}/retry/simulation`);
        } catch (error) {
            console.error('Failed to reset simulations:', error);
        }
        setSessionData(prev => ({ ...prev, completion: null }));
        setCompletionError(null);
        setSimStarted(false);
        setSimComplete(false);
        setSimConversation([]);
        setSimEvaluation(null);
        setCurrentSimIndex(0);
        setDisplayContent({ title: '', text: '', keyPoints: [], tips: [] });
        setCurrentStage(STAGES.SIMULATION);
    };

    // ==================== RENDER HELPERS ====================
    
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const getStageInfo = () => {
        const stages = [
            { id: STAGES.INTRO, label: 'Intro', icon: BookOpen },
            { id: STAGES.TEACHING, label: 'Learn', icon: Lightbulb },
            { id: STAGES.QUIZ, label: 'Quiz', icon: HelpCircle },
            { id: STAGES.SIMULATION, label: 'Practice', icon: Users },
            { id: STAGES.COMPLETE, label: 'Done', icon: CheckCircle2 }
        ];
        const currentIndex = stages.findIndex(s => s.id === currentStage);
        return { stages, currentIndex };
    };

    // ==================== AUDIO STATE INDICATOR ====================
    
    const renderAudioStateIndicator = () => {
        if (audioState === AUDIO_STATE.AI_SPEAKING) {
            return (
                <div className="flex items-center gap-3 p-4 rounded-xl bg-indigo-500/10 border border-indigo-500/20">
                    <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                            <div 
                                key={`wave-${i}`}
                                className="w-1 bg-indigo-400 rounded-full animate-pulse"
                                style={{
                                    height: `${Math.random() * 16 + 8}px`,
                                    animationDelay: `${i * 0.1}s`
                                }}
                            />
                        ))}
                    </div>
                    <span className="text-indigo-400 font-medium">AI is speaking...</span>
                    <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={stopAudio}
                        className="ml-auto text-indigo-400 hover:text-indigo-300"
                    >
                        <StopCircle className="w-4 h-4 mr-1" />
                        Stop
                    </Button>
                </div>
            );
        }
        
        if (audioState === AUDIO_STATE.USER_RECORDING) {
            return (
                <div className="flex items-center gap-3 p-4 rounded-xl bg-rose-500/10 border border-rose-500/20">
                    <div className="w-3 h-3 bg-rose-500 rounded-full animate-pulse" />
                    <span className="text-rose-400 font-medium">Recording: {formatTime(recordingTime)}</span>
                    <div className="flex items-center gap-1 ml-2">
                        {[...Array(8)].map((_, i) => (
                            <div 
                                key={`rec-wave-${i}`}
                                className="w-1 bg-rose-400 rounded-full animate-pulse"
                                style={{
                                    height: `${Math.random() * 20 + 4}px`,
                                    animationDelay: `${i * 0.05}s`
                                }}
                            />
                        ))}
                    </div>
                </div>
            );
        }
        
        if (audioState === AUDIO_STATE.PROCESSING) {
            return (
                <div className="flex items-center gap-3 p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
                    <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
                    <span className="text-blue-400 font-medium">Processing your response...</span>
                </div>
            );
        }
        
        return null;
    };

    // ==================== VOICE INPUT CONTROLS ====================
    
    const renderVoiceControls = (onSubmit, placeholder = "Click to record your response") => {
        const isRecording = audioState === AUDIO_STATE.USER_RECORDING;
        const isDisabled = audioState === AUDIO_STATE.AI_SPEAKING || audioState === AUDIO_STATE.PROCESSING;
        
        return (
            <div className="space-y-4">
                {/* Current Transcript */}
                {currentTranscript && !isRecording && (
                    <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                        <p className="text-xs text-white/50 uppercase tracking-wider mb-2">Your Response:</p>
                        <p className="text-white/90">&ldquo;{currentTranscript}&rdquo;</p>
                        <p className="text-xs text-white/50 mt-2">{currentTranscript.split(/\s+/).length} words</p>
                    </div>
                )}
                
                {/* Error Display */}
                {audioError && (
                    <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/20 text-rose-400 text-sm">
                        {audioError}
                    </div>
                )}
                
                {/* Recording Button */}
                <div className="flex items-center justify-center gap-4">
                    {!isRecording ? (
                        <Button
                            size="lg"
                            onClick={startRecording}
                            disabled={isDisabled || stageLoading}
                            className={`px-8 py-6 text-lg ${
                                isDisabled 
                                    ? 'bg-gray-600 cursor-not-allowed' 
                                    : 'bg-indigo-600 hover:bg-indigo-700'
                            }`}
                            data-testid="start-recording-btn"
                        >
                            <Mic className="w-5 h-5 mr-2" />
                            {isDisabled ? 'Wait for AI...' : 'Start Recording'}
                        </Button>
                    ) : (
                        <Button
                            size="lg"
                            onClick={onSubmit}
                            disabled={recordingTime < 2}
                            className="bg-emerald-600 hover:bg-emerald-700 px-8 py-6 text-lg"
                            data-testid="stop-recording-btn"
                        >
                            <Square className="w-5 h-5 mr-2" />
                            {recordingTime < 2 ? `Speak for ${2 - recordingTime}s...` : 'Stop & Submit'}
                        </Button>
                    )}
                </div>
                
                {/* Instructions */}
                {!isRecording && audioState === AUDIO_STATE.IDLE && !currentTranscript && (
                    <p className="text-center text-white/50 text-sm">{placeholder}</p>
                )}
            </div>
        );
    };

    // ==================== CONTENT DISPLAY PANEL ====================
    
    const renderContentPanel = () => {
        if (!displayContent.text && displayContent.keyPoints.length === 0) {
            return null;
        }
        
        return (
            <div className="space-y-4 p-6 rounded-2xl bg-black/40 backdrop-blur-xl border border-white/10">
                {displayContent.title && (
                    <h3 className="text-xl font-bold text-white">{displayContent.title}</h3>
                )}
                
                {displayContent.text && (
                    <div className="prose prose-invert prose-sm max-w-none">
                        <p className="text-white/80 leading-relaxed whitespace-pre-wrap">
                            {displayContent.text}
                        </p>
                    </div>
                )}
                
                {displayContent.keyPoints.length > 0 && (
                    <div className="mt-4 p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/20">
                        <h4 className="text-sm font-semibold text-emerald-400 mb-2 flex items-center gap-2">
                            <Target className="w-4 h-4" />
                            Key Points
                        </h4>
                        <ul className="space-y-2">
                            {displayContent.keyPoints.map((point, i) => (
                                <li key={`kp-${i}`} className="text-sm text-white/70 flex items-start gap-2">
                                    <span className="text-emerald-400 mt-1">•</span>
                                    {point}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                
                {displayContent.tips.length > 0 && (
                    <div className="mt-4 p-4 rounded-xl bg-amber-500/5 border border-amber-500/20">
                        <h4 className="text-sm font-semibold text-amber-400 mb-2 flex items-center gap-2">
                            <Lightbulb className="w-4 h-4" />
                            Notes
                        </h4>
                        <ul className="space-y-2">
                            {displayContent.tips.map((tip, i) => (
                                <li key={`tip-${i}`} className="text-sm text-white/70 flex items-start gap-2">
                                    <span className="text-amber-400 mt-1">•</span>
                                    {tip}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        );
    };

    // ==================== STAGE PROGRESS BAR ====================
    
    const renderStageProgress = () => {
        const { stages, currentIndex } = getStageInfo();
        
        return (
            <div className="flex items-center justify-between mb-6 p-4 rounded-xl bg-white/5 border border-white/10">
                {stages.map((stage, index) => {
                    const Icon = stage.icon;
                    const isActive = index === currentIndex;
                    const isComplete = index < currentIndex;
                    
                    return (
                        <div key={stage.id} className="flex items-center">
                            <div className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                                isActive 
                                    ? 'bg-indigo-500 text-white' 
                                    : isComplete 
                                    ? 'bg-emerald-500/20 text-emerald-400' 
                                    : 'bg-white/5 text-white/40'
                            }`}>
                                <Icon className="w-4 h-4" />
                                <span className="text-sm font-medium hidden md:inline">{stage.label}</span>
                            </div>
                            {index < stages.length - 1 && (
                                <ChevronRight className={`w-5 h-5 mx-2 ${
                                    isComplete ? 'text-emerald-400' : 'text-white/20'
                                }`} />
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    // ==================== MAIN RENDER ====================

    if (loading) {
        return (
            <div className="min-h-screen bg-[hsl(240,10%,4%)] flex items-center justify-center">
                <Loader2 className="w-12 h-12 animate-spin text-indigo-400" />
            </div>
        );
    }

    if (!sessionData) {
        return (
            <div className="min-h-screen bg-[hsl(240,10%,4%)] text-white text-center py-12">
                <XCircle className="w-16 h-16 mx-auto text-rose-500 mb-4" />
                <h2 className="text-xl font-semibold mb-2">Session Not Found</h2>
                <Button onClick={() => navigate('/inside-sales')}>Back to Dashboard</Button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[hsl(240,10%,4%)] text-white py-8 px-6" data-testid="inside-sales-session">
            <div className="max-w-5xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <Button 
                    variant="ghost" 
                    onClick={() => navigate('/inside-sales')}
                    className="text-white/70 hover:text-white hover:bg-white/10"
                >
                    <ChevronLeft className="w-4 h-4 mr-2" />
                    Back to Dashboard
                </Button>
                
                <Badge variant="outline" className="text-white/70 border-white/20">
                    Session {sessionId} of 20
                </Badge>
            </div>

            {/* Session Title */}
            <div>
                <h1 className="text-2xl md:text-3xl font-bold text-white">
                    {sessionData.session?.title}
                </h1>
                <p className="text-white/60 mt-1">
                    {sessionData.session?.description}
                </p>
            </div>

            {/* Stage Progress */}
            {renderStageProgress()}

            {/* Audio State Indicator */}
            {renderAudioStateIndicator()}

            {/* Main Content Area */}
            <div className="grid lg:grid-cols-12 gap-6">
                {/* Content Panel (Left/Main) */}
                <div className="lg:col-span-8">
                    {renderContentPanel()}
                    
                    {/* Stage-specific content */}
                    {currentStage === STAGES.INTRO && !displayContent.text && (
                        <div className="text-center p-12 rounded-2xl bg-white/5 border border-white/10">
                            <BookOpen className="w-16 h-16 mx-auto text-indigo-400 mb-4" />
                            <h2 className="text-2xl font-bold text-white mb-2">Ready to Begin?</h2>
                            <p className="text-white/60 mb-6 max-w-md mx-auto">
                                Click play to hear the introduction. The content will appear here so you can follow along.
                            </p>
                            <Button 
                                onClick={playIntro} 
                                disabled={stageLoading}
                                className="bg-indigo-600 hover:bg-indigo-700 px-8"
                            >
                                {stageLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                                Play Introduction
                            </Button>
                        </div>
                    )}

                    {/* Simulation Conversation */}
                    {currentStage === STAGES.SIMULATION && simStarted && simConversation.length > 0 && (
                        <div className="space-y-4 mt-4">
                            <div className="max-h-64 overflow-y-auto space-y-4">
                                {simConversation.map((msg, i) => (
                                    <div 
                                        key={`msg-${i}`}
                                        className={`flex ${msg.speaker === 'user' ? 'justify-end' : 'justify-start'}`}
                                    >
                                        <div className={`max-w-[85%] p-4 rounded-2xl ${
                                            msg.speaker === 'user' 
                                                ? 'bg-indigo-500/20 border border-indigo-500/30' 
                                                : 'bg-white/5 border border-white/10'
                                        }`}>
                                            <p className="text-xs text-white/50 mb-1">
                                                {msg.speaker === 'user' ? 'You' : 'Client'}
                                            </p>
                                            <p className="text-white/90">{msg.content}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            
                            {/* Inline Speech Coaching during simulation */}
                            {!simComplete && (speechAnalytics || isSpeechAnalyzing) && (
                                <SpeechCoaching 
                                    analytics={speechAnalytics}
                                    isLoading={isSpeechAnalyzing}
                                    compact={true}
                                />
                            )}
                        </div>
                    )}

                    {/* Simulation Evaluation */}
                    {currentStage === STAGES.SIMULATION && simComplete && simEvaluation && (
                        <div className="mt-6 space-y-4">
                            <ConstructiveFeedback
                                type="simulation"
                                score={simEvaluation.overall_score || 0}
                                strengths={simEvaluation.strengths || []}
                                improvements={simEvaluation.improvements || []}
                                overallFeedback={simEvaluation.feedback || ''}
                                actionableAdvice={simEvaluation.tips || []}
                                onContinue={nextSimulation}
                                continueLabel={currentSimIndex < simulations.length - 1 ? 'Next Simulation' : 'Complete Session'}
                            />
                            
                            {/* Speech Coaching - Real-time feedback on delivery */}
                            {(speechAnalytics || isSpeechAnalyzing) && (
                                <SpeechCoaching 
                                    analytics={speechAnalytics}
                                    isLoading={isSpeechAnalyzing}
                                    compact={true}
                                    onClose={clearSpeechAnalytics}
                                />
                            )}
                        </div>
                    )}

                    {/* Quiz Answer Feedback */}
                    {currentStage === STAGES.QUIZ && answerFeedback && (
                        <div className="mt-6 space-y-4">
                            <ConstructiveFeedback
                                type="quiz"
                                score={answerFeedback.score || 0}
                                strengths={answerFeedback.strengths || []}
                                improvements={answerFeedback.improvements || []}
                                overallFeedback={answerFeedback.feedback || ''}
                                actionableAdvice={answerFeedback.correct_answer_hint ? [`Hint: ${answerFeedback.correct_answer_hint}`] : []}
                                onContinue={nextQuestion}
                                continueLabel={currentQuestionIndex < quizQuestions.length - 1 ? 'Next Question' : 'Complete Quiz'}
                            />
                            
                            {/* Speech Coaching - Real-time feedback on delivery */}
                            {(speechAnalytics || isSpeechAnalyzing) && (
                                <SpeechCoaching 
                                    analytics={speechAnalytics}
                                    isLoading={isSpeechAnalyzing}
                                    compact={true}
                                    onClose={clearSpeechAnalytics}
                                />
                            )}
                        </div>
                    )}

                    {/* Quiz Not Passed */}
                    {currentStage === STAGES.QUIZ && quizResult && !quizResult.passed && (
                        <RetryPanel
                            testId="quiz-not-passed-panel"
                            title="Quiz Not Passed Yet"
                            message="You're close! Review the feedback and try the quiz again. Only your latest answer to each question counts."
                            score={quizResult.quiz_score}
                            threshold={quizResult.threshold}
                            actions={[
                                { label: 'Review Lesson', variant: 'outline', testId: 'quiz-review-lesson-btn', onClick: () => { setQuizResult(null); setCurrentStage(STAGES.TEACHING); loadTeachingSection(0); } },
                                { label: 'Retry Quiz', testId: 'quiz-retry-btn', onClick: retryQuiz }
                            ]}
                        />
                    )}

                    {/* Session Not Passed / Save Error */}
                    {currentStage === STAGES.COMPLETE && (completionError || sessionData.completion?.session_completed === false) && (
                        <RetryPanel
                            testId="session-not-passed-panel"
                            title={completionError ? 'Could Not Save Session' : 'Session Not Passed Yet'}
                            message={completionError || 'Your combined quiz and practice score is below the passing mark. Retry the part you want to improve.'}
                            score={completionError ? null : sessionData.completion?.overall_score}
                            threshold={sessionData.completion?.threshold}
                            actions={completionError
                                ? [{ label: 'Try Again', testId: 'session-complete-retry-btn', onClick: completeSession }]
                                : [
                                    { label: 'Retry Quiz', variant: 'outline', testId: 'session-retry-quiz-btn', onClick: retryQuiz },
                                    { label: 'Retry Practice', testId: 'session-retry-practice-btn', onClick: retrySimulations }
                                ]}
                        />
                    )}

                    {/* Session Complete */}
                    {currentStage === STAGES.COMPLETE && !completionError && sessionData.completion?.session_completed !== false && (
                        <div className="text-center p-8 md:p-12 rounded-2xl bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/20" data-testid="session-complete-panel">
                            <div className="w-20 h-20 mx-auto bg-emerald-500/20 rounded-full flex items-center justify-center mb-6">
                                <CheckCircle2 className="w-10 h-10 text-emerald-400" />
                            </div>
                            <h2 className="text-3xl font-bold text-white mb-2">Session Complete!</h2>
                            <p className="text-white/60 mb-6">Great work on completing Session {sessionId}!</p>
                            
                            {sessionData.completion && (
                                <>
                                    {/* Main Scores Grid */}
                                    <div className="grid grid-cols-3 gap-4 max-w-md mx-auto mb-6">
                                        <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                                            <p className="text-2xl font-bold text-indigo-400">
                                                {Math.round((sessionData.completion.overall_score || 0) * 100)}%
                                            </p>
                                            <p className="text-xs text-white/50">Overall</p>
                                        </div>
                                        <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                                            <p className="text-2xl font-bold text-blue-400">
                                                {Math.round((sessionData.completion.quiz_score || 0) * 100)}%
                                            </p>
                                            <p className="text-xs text-white/50">Quiz</p>
                                        </div>
                                        <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                                            <p className="text-2xl font-bold text-purple-400">
                                                {Math.round((sessionData.completion.simulation_score || 0) * 100)}%
                                            </p>
                                            <p className="text-xs text-white/50">Simulation</p>
                                        </div>
                                    </div>
                                    
                                    {/* Voice & Communication Scores */}
                                    {(sessionData.completion.voice_clarity || sessionData.completion.communication_score) && (
                                        <div className="grid grid-cols-2 gap-4 max-w-sm mx-auto mb-6">
                                            {sessionData.completion.voice_clarity && (
                                                <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20">
                                                    <div className="flex items-center justify-center gap-2 mb-1">
                                                        <Mic className="w-4 h-4 text-rose-400" />
                                                        <p className="text-xl font-bold text-rose-400">
                                                            {Math.round(sessionData.completion.voice_clarity * 100)}%
                                                        </p>
                                                    </div>
                                                    <p className="text-xs text-white/50">Voice Clarity</p>
                                                </div>
                                            )}
                                            {sessionData.completion.communication_score && (
                                                <div className="p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
                                                    <div className="flex items-center justify-center gap-2 mb-1">
                                                        <MessageCircle className="w-4 h-4 text-cyan-400" />
                                                        <p className="text-xl font-bold text-cyan-400">
                                                            {Math.round(sessionData.completion.communication_score * 100)}%
                                                        </p>
                                                    </div>
                                                    <p className="text-xs text-white/50">Communication</p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    
                                    {/* Points Earned */}
                                    {sessionData.completion.points_earned && (
                                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20 mb-6">
                                            <Star className="w-5 h-5 text-amber-400" />
                                            <span className="text-amber-400 font-semibold">
                                                +{sessionData.completion.points_earned} XP Earned
                                            </span>
                                        </div>
                                    )}
                                </>
                            )}
                            
                            <div className="flex justify-center gap-4">
                                <Button 
                                    variant="outline" 
                                    onClick={() => navigate('/inside-sales')}
                                    className="border-white/20 text-white hover:bg-white/10"
                                >
                                    Back to Dashboard
                                </Button>
                                {sessionData.completion?.next_session && (
                                    <Button 
                                        onClick={() => navigate(`/inside-sales/session/${sessionData.completion.next_session}`)}
                                        className="bg-indigo-600 hover:bg-indigo-700"
                                        data-testid="next-session-btn"
                                    >
                                        Next Session
                                        <ChevronRight className="w-4 h-4 ml-2" />
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Controls Panel (Right) */}
                <div className="lg:col-span-4 space-y-4">
                    {/* Navigation Controls */}
                    {currentStage === STAGES.INTRO && displayContent.text && (
                        <Card className="bg-white/5 border-white/10">
                            <CardContent className="p-4 space-y-4">
                                <Button 
                                    onClick={playIntro}
                                    variant="outline"
                                    className="w-full border-white/20 text-white hover:bg-white/10"
                                    disabled={audioState === AUDIO_STATE.AI_SPEAKING}
                                >
                                    <RotateCcw className="w-4 h-4 mr-2" />
                                    Replay Intro
                                </Button>
                                <Button 
                                    onClick={completeIntro}
                                    className="w-full bg-indigo-600 hover:bg-indigo-700"
                                >
                                    Continue to Learning
                                    <ChevronRight className="w-4 h-4 ml-2" />
                                </Button>
                            </CardContent>
                        </Card>
                    )}

                    {/* Teaching Controls */}
                    {currentStage === STAGES.TEACHING && (
                        <Card className="bg-white/5 border-white/10">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm text-white/70">
                                    Section {currentSectionIndex + 1} of {teachingSections.length}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-4 space-y-4">
                                <Progress 
                                    value={((currentSectionIndex + 1) / teachingSections.length) * 100} 
                                    className="h-2 bg-white/10"
                                />
                                <div className="flex gap-2">
                                    <Button 
                                        onClick={() => loadTeachingSection(currentSectionIndex)}
                                        variant="outline"
                                        size="sm"
                                        className="flex-1 border-white/20 text-white hover:bg-white/10"
                                        disabled={audioState === AUDIO_STATE.AI_SPEAKING}
                                    >
                                        <RotateCcw className="w-4 h-4 mr-1" />
                                        Replay
                                    </Button>
                                    <Button 
                                        onClick={nextTeachingSection}
                                        size="sm"
                                        className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                                    >
                                        {currentSectionIndex < teachingSections.length - 1 ? 'Next' : 'Start Quiz'}
                                        <ChevronRight className="w-4 h-4 ml-1" />
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Quiz Voice Controls */}
                    {currentStage === STAGES.QUIZ && !answerFeedback && !quizResult && (
                        <Card className="bg-white/5 border-white/10">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm text-white/70 flex items-center gap-2">
                                    <HelpCircle className="w-4 h-4" />
                                    Question {currentQuestionIndex + 1} of {quizQuestions.length}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-4">
                                {renderVoiceControls(
                                    handleQuizRecordingStop,
                                    "Speak your answer clearly..."
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* Simulation Controls */}
                    {currentStage === STAGES.SIMULATION && !simComplete && (
                        <Card className="bg-white/5 border-white/10">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm text-white/70 flex items-center gap-2">
                                    <Users className="w-4 h-4" />
                                    Simulation {currentSimIndex + 1} of {simulations.length}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-4">
                                {!simStarted ? (
                                    <div className="space-y-4">
                                        <p className="text-sm text-white/70">
                                            {simulations[currentSimIndex]?.description}
                                        </p>
                                        <Button 
                                            onClick={startSimulation}
                                            disabled={stageLoading}
                                            className="w-full bg-indigo-600 hover:bg-indigo-700"
                                            data-testid="start-simulation-btn"
                                        >
                                            {stageLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                                            {canFinishSession ? 'Redo Simulation' : 'Start Simulation'}
                                        </Button>
                                        {canFinishSession && (
                                            <Button
                                                onClick={completeSession}
                                                variant="outline"
                                                className="w-full border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                                                data-testid="finish-session-btn"
                                            >
                                                <CheckCircle2 className="w-4 h-4 mr-2" />
                                                Finish Session
                                            </Button>
                                        )}
                                    </div>
                                ) : (
                                    renderVoiceControls(
                                        handleSimulationRecordingStop,
                                        "Respond to the client..."
                                    )
                                )}
                            </CardContent>
                        </Card>
                    )}
                    
                    {/* Live Score Card - Shows during quiz and simulation stages */}
                    {(currentStage === STAGES.QUIZ || currentStage === STAGES.SIMULATION) && (
                        <LiveScoreCard
                            voiceScore={speechAnalytics?.overallScore || liveScores.voiceScore}
                            communicationScore={Math.round((liveScores.quizScore + liveScores.simulationScore) / 2) || 0}
                            overallScore={liveScores.overallScore}
                            showBreakdown={true}
                        />
                    )}
                </div>
            </div>
            
            {/* Achievement Popup */}
            {earnedAchievement && (
                <AchievementPopup
                    badge={earnedAchievement}
                    onClose={() => setEarnedAchievement(null)}
                />
            )}
            </div>
        </div>
    );
};

export default InsideSalesSession;
